import {menuAnatomy} from '@chakra-ui/anatomy';
import {createMultiStyleConfigHelpers, defineCssVars, useMultiStyleConfig} from '@chakra-ui/react';
import {mode} from '@chakra-ui/theme-tools';

import {semantic as semanticDark, components as componentsDark} from '../../design-tokens/dark';
import {components, core, semantic} from '../../design-tokens/light';
import {getCoreValueAsThemeToken} from '../types';

const parts = menuAnatomy.keys;
export type ContextMenuParts = (typeof menuAnatomy.keys)[number];

const {
  padding: $padding,
  radius: $radius,
  bg: $bg,
  shadow: $shadow,
} = defineCssVars(`context-menu`, [`padding`, `radius`, `bg`, `shadow`]);

const {definePartsStyle, defineMultiStyleConfig} = createMultiStyleConfigHelpers(parts);

const baseStyle = definePartsStyle(props => ({
  list: {
    [$padding.variable]: `space.${getCoreValueAsThemeToken(core, `space.space-100`)}`,
    [$radius.variable]: `radii.${getCoreValueAsThemeToken(core, `radii.radius-150`)}`,
    [$bg.variable]: `colors.${mode(
      semantic.colors.background[`elevation-2`],
      semanticDark.colors.background[`elevation-2`],
    )(props)}`,
    [$shadow.variable]: `shadows.lg`,
    boxShadow: $shadow.reference,
    padding: $padding.reference,
    borderRadius: $radius.reference,
    gap: `space.${getCoreValueAsThemeToken(core, `space.space-150`)}`,
    bg: $bg.reference,
    maxWidth: `16rem`,
  },
  button: {
    [`&[aria-expanded=true]`]: {
      bg: mode(
        components.colors.components.nav[`background-selected`],
        componentsDark.colors.components.nav[`background-selected`],
      )(props),
    },
  },
  item: {
    color: mode(semantic.colors.text.primary, semanticDark.colors.text.primary)(props),
    // Body Medium text style
    fontSize: `md`,
    fontWeight: `normal`,
    lineHeight: `1.5rem`,
    minWidth: `15rem`,
    backgroundColor: $bg.reference,
    py: getCoreValueAsThemeToken(core, `space.space-100`),
    px: getCoreValueAsThemeToken(core, `space.space-150`),
    gap: getCoreValueAsThemeToken(core, `space.space-150`),

    _hover: {
      bg: mode(
        components.colors.components.dropdown[`background-hover`],
        componentsDark.colors.components.dropdown[`background-hover`],
      )(props),
    },

    [`&:first-of-type`]: {
      borderTopRadius: $radius.reference,
    },
    [`&:last-of-type`]: {
      borderBottomRadius: $radius.reference,
    },
    [`&&`]: {
      textDecoration: `none`,
    },
  },
  icon: {
    marginInlineEnd: 0,
  },
}));

export const contextMenuTheme = defineMultiStyleConfig({
  baseStyle,
});

export const useContextMenuStyleConfig = (
  part: ContextMenuParts,
  props?: Parameters<typeof useMultiStyleConfig>[1],
) => {
  return useMultiStyleConfig(`ContextMenu`, props ?? {})?.[part];
};
